import {
  ChevronDown,
  Home,
  LogOut,
  Menu,
  Search as SearchIcon,
  Settings as SettingsIcon,
  UserCircle,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import * as ReactDOM from 'react-dom';
import {
  type NavigateFunction,
  NavLink,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { AccountDisabledBanner } from './components/AccountDisabledBanner';
import CasesView from './components/CasesView';
import DetailView from './components/DetailView';
import ForgotPassword from './components/ForgotPassword';
import Login from './components/Login';
import Logo from './components/Logo';
import Onboarding from './components/Onboarding';
import Profile from './components/Profile';
import ProtectedRoute, { OnboardingRoute, OwnerRoute } from './components/ProtectedRoute';
import Searches from './components/Searches';
import Settings from './components/Settings';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useScrollToTopOnNavigate } from './hooks/useScrollToTopOnNavigate';
import { cn } from './lib/utils';

function navigateWithTransition(navigate: NavigateFunction, path: string) {
  if (document.startViewTransition) {
    document.startViewTransition(() => {
      ReactDOM.flushSync(() => navigate(path));
    });
  } else {
    navigate(path);
  }
}

function UserMenu() {
  const { session, role, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const email = session?.user?.email ?? '';
  const initial = email[0]?.toUpperCase() ?? '?';

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const go = (path: string) => {
    setOpen(false);
    navigateWithTransition(navigate, path);
  };

  return (
    <div className="relative ml-2" ref={ref}>
      <button
        className={cn(
          'inline-flex items-center gap-2 px-3 py-2 bg-transparent border border-transparent rounded-sm cursor-pointer text-ink-secondary text-[0.8125rem] font-medium font-sans transition-[background-color,color,border-color] duration-150',
          open
            ? 'bg-nav-hover text-ink border-border-emphasis'
            : 'hover:bg-nav-hover hover:text-ink'
        )}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {/* Avatar */}
        <span className="w-[22px] h-[22px] rounded-full bg-accent-subtle border border-accent text-accent text-[0.6875rem] font-bold flex items-center justify-center flex-shrink-0 font-sans">
          {initial}
        </span>
        <span className="max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap">
          {email}
        </span>
        <ChevronDown
          size={13}
          className={cn(
            'flex-shrink-0 text-ink-tertiary transition-transform duration-150',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <div className="absolute top-[calc(100%+6px)] right-0 min-w-[200px] bg-surface-raised border border-border-emphasis rounded-md shadow-[0_8px_24px_rgba(0,0,0,0.4)] z-[100] overflow-hidden animate-menu-in">
          <div className="px-4 py-3">
            <span className="text-[0.75rem] text-ink-tertiary block overflow-hidden text-ellipsis whitespace-nowrap">
              {email}
            </span>
          </div>
          <div className="h-px bg-border m-0" />
          <button
            className="flex items-center gap-3 w-full px-4 py-3 bg-transparent border-none cursor-pointer text-ink-secondary text-[0.8125rem] font-medium font-sans text-left hover:bg-nav-hover hover:text-ink transition-[background-color,color] duration-150"
            onClick={() => go('/perfil')}
          >
            <UserCircle size={15} /> Perfil
          </button>
          <button
            className="flex items-center gap-3 w-full px-4 py-3 bg-transparent border-none cursor-pointer text-ink-secondary text-[0.8125rem] font-medium font-sans text-left hover:bg-nav-hover hover:text-ink transition-[background-color,color] duration-150"
            onClick={() => go('/busquedas')}
          >
            <SearchIcon size={15} /> Búsquedas
          </button>
          {role === 'owner' && (
            <button
              className="flex items-center gap-3 w-full px-4 py-3 bg-transparent border-none cursor-pointer text-ink-secondary text-[0.8125rem] font-medium font-sans text-left hover:bg-nav-hover hover:text-ink transition-[background-color,color] duration-150"
              onClick={() => go('/settings')}
            >
              <SettingsIcon size={15} /> Configuración
            </button>
          )}
          <div className="h-px bg-border m-0" />
          <button
            className="flex items-center gap-3 w-full px-4 py-3 bg-transparent border-none cursor-pointer text-destructive text-[0.8125rem] font-medium font-sans text-left hover:bg-destructive-subtle transition-[background-color] duration-150"
            onClick={signOut}
          >
            <LogOut size={15} /> Salir
          </button>
        </div>
      )}
    </div>
  );
}

interface AuthenticatedLayoutProps {
  children: ReactNode;
}

function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  const { session, role, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const email = session?.user?.email ?? '';

  useScrollToTopOnNavigate(mainRef);

  // biome-ignore lint/correctness/useExhaustiveDependencies: setMobileMenuOpen es estable (setter de useState)
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const goMobile = (path: string) => {
    setMobileMenuOpen(false);
    navigateWithTransition(navigate, path);
  };

  return (
    <div className="h-full flex flex-col">
      <nav className="bg-terminal-bg border-b border-border-strong shrink-0">
        <div className="safe-top" />
        <div className="text-ink px-4 sm:px-10 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <Logo size={26} className="text-accent shrink-0" />
            <h1 className="font-serif text-[1.25rem] font-semibold tracking-tight text-accent">
              Legal Hermes
            </h1>
          </div>
          {/* Desktop nav */}
          <div className="hidden sm:flex gap-1 items-center">
            <NavLink
              to="/"
              end
              viewTransition
              className={({ isActive }) =>
                cn(
                  'px-3 py-1.5 rounded-sm no-underline text-[0.8125rem] font-medium inline-flex items-center gap-2 tracking-wide transition-[background-color,color] duration-150',
                  isActive
                    ? 'bg-accent-subtle text-accent'
                    : 'text-ink-secondary hover:bg-nav-hover hover:text-ink'
                )
              }
            >
              <Home size={14} /> Inicio
            </NavLink>
            <UserMenu />
          </div>
          {/* Mobile hamburger */}
          <button
            className="sm:hidden p-2 rounded-sm text-ink-secondary hover:text-ink hover:bg-nav-hover transition-[background-color,color] duration-150 border-none bg-transparent cursor-pointer"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Menú"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileMenuOpen && (
        <div className="sm:hidden bg-terminal-bg border-b border-border-strong px-4 py-3 flex flex-col gap-1 shrink-0">
          <NavLink
            to="/"
            end
            viewTransition
            className={({ isActive }) =>
              cn(
                'w-full px-3 py-2.5 rounded-sm no-underline text-[0.875rem] font-medium inline-flex items-center gap-2 tracking-wide transition-[background-color,color] duration-150',
                isActive
                  ? 'bg-accent-subtle text-accent'
                  : 'text-ink-secondary hover:bg-nav-hover hover:text-ink'
              )
            }
            onClick={() => setMobileMenuOpen(false)}
          >
            <Home size={15} /> Inicio
          </NavLink>
          <div className="h-px bg-border my-1" />
          <span className="px-3 py-1 text-[0.75rem] text-ink-muted overflow-hidden text-ellipsis whitespace-nowrap">
            {email}
          </span>
          <button
            className="w-full flex items-center gap-3 px-3 py-2.5 bg-transparent border-none cursor-pointer text-ink-secondary text-[0.875rem] font-medium font-sans text-left hover:bg-nav-hover hover:text-ink transition-[background-color,color] duration-150 rounded-sm"
            onClick={() => goMobile('/perfil')}
          >
            <UserCircle size={15} /> Perfil
          </button>
          <button
            className="w-full flex items-center gap-3 px-3 py-2.5 bg-transparent border-none cursor-pointer text-ink-secondary text-[0.875rem] font-medium font-sans text-left hover:bg-nav-hover hover:text-ink transition-[background-color,color] duration-150 rounded-sm"
            onClick={() => goMobile('/busquedas')}
          >
            <SearchIcon size={15} /> Búsquedas
          </button>
          {role === 'owner' && (
            <button
              className="w-full flex items-center gap-3 px-3 py-2.5 bg-transparent border-none cursor-pointer text-ink-secondary text-[0.875rem] font-medium font-sans text-left hover:bg-nav-hover hover:text-ink transition-[background-color,color] duration-150 rounded-sm"
              onClick={() => goMobile('/settings')}
            >
              <SettingsIcon size={15} /> Configuración
            </button>
          )}
          <div className="h-px bg-border my-1" />
          <button
            className="w-full flex items-center gap-3 px-3 py-2.5 bg-transparent border-none cursor-pointer text-destructive text-[0.875rem] font-medium font-sans text-left hover:bg-destructive-subtle transition-[background-color] duration-150 rounded-sm"
            onClick={() => {
              setMobileMenuOpen(false);
              signOut();
            }}
          >
            <LogOut size={15} /> Salir
          </button>
        </div>
      )}

      <AccountDisabledBanner />

      <main
        ref={mainRef}
        className="flex-1 overflow-y-auto bg-parchment safe-bottom"
        style={{ viewTransitionName: 'page-content' }}
      >
        <div className="max-w-5xl mx-auto px-4 py-6 sm:px-10 sm:py-10">{children}</div>
      </main>
    </div>
  );
}

function NotFound() {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
      <span className="font-mono text-[4rem] font-bold text-border-strong leading-none">404</span>
      <div className="flex flex-col gap-1.5">
        <p className="text-[1.0625rem] text-ink font-medium">Página no encontrada</p>
        <p className="text-[0.875rem] text-ink-muted">
          La ruta solicitada no existe en esta aplicación.
        </p>
      </div>
      <button
        onClick={() => navigate('/')}
        className="px-4 py-2 border border-border-emphasis text-ink-secondary text-[0.8125rem] rounded-sm bg-transparent cursor-pointer hover:text-ink hover:border-border-strong transition-[border-color,color] duration-150"
      >
        Volver al inicio
      </button>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/onboarding"
            element={
              <OnboardingRoute>
                <Onboarding />
              </OnboardingRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <CasesView />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <OwnerRoute>
                <AuthenticatedLayout>
                  <Settings />
                </AuthenticatedLayout>
              </OwnerRoute>
            }
          />
          <Route
            path="/busquedas"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Searches />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/perfil"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <Profile />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/detalle/:type/:id"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <DetailView />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <NotFound />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
